/**
 * [TD-47.3] useDraftQueue - single-source-of-truth hook for the snake
 * draft queue. Locked verdicts:
 *
 *   • Gap 6 - localStorage scoping: `pgf_draft_queue:{league_id}:{user_id}`,
 *     cap 100 entries, wipe on terminal state (DRAFT_COMPLETE).
 *   • Gap 4 - Auto-remove diff watcher subscribes to useSnakeDraft picks;
 *     emits sonner toast when ANOTHER team drafts a queued player; silent
 *     prune when the user drafts their own queued player.
 *   • Gap 9 - Diff watcher lives inside the hook (single mount point);
 *     consumers do not re-implement.
 *
 * Hook is mounted exactly once via DraftQueueProvider; consumers read via
 * `useDraftQueueContext()`. This avoids racing localStorage writes from
 * parallel hook instances and keeps the diff-watcher singleton.
 *
 * Return shape:
 *   {
 *     queue: Array<QueuedPlayer>,           // ordered, top of queue at index 0
 *     addToQueue(player),                   // append (no-op if already queued or cap hit)
 *     removeFromQueue(playerId),
 *     reorderQueue(fromIdx, toIdx),         // drag/drop reorder
 *     moveUp(playerId), moveDown(playerId), // chevron a11y fallback
 *     clearQueue(),
 *     isQueued(playerId): boolean,
 *     topOfQueue: QueuedPlayer | null,
 *     queueCapped: boolean,                 // true once queue.length === MAX_QUEUE
 *   }
 *
 * QueuedPlayer = { id, name, position, nfl_team, bye_week, projected_points }
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useSnakeDraft } from "../contexts/SnakeDraftContext";

const STORAGE_PREFIX = "pgf_draft_queue";
export const MAX_QUEUE = 100;

const storageKey = (leagueId, userId) =>
  `${STORAGE_PREFIX}:${leagueId || "_"}:${userId || "_"}`;

/** Hydrate the persisted queue (best-effort; corrupt JSON = empty array). */
function hydrate(leagueId, userId) {
  if (!leagueId || !userId) return [];
  try {
    const raw = localStorage.getItem(storageKey(leagueId, userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Soft-validate each row + enforce cap on hydrate.
    return parsed
      .filter((p) => p && typeof p.id === "string")
      .slice(0, MAX_QUEUE);
  } catch {
    return [];
  }
}

function persist(leagueId, userId, queue) {
  if (!leagueId || !userId) return;
  try {
    localStorage.setItem(
      storageKey(leagueId, userId),
      JSON.stringify(queue.slice(0, MAX_QUEUE)),
    );
  } catch {
    /* quota / privacy mode - accept loss, in-memory still works */
  }
}

function wipe(leagueId, userId) {
  if (!leagueId || !userId) return;
  try {
    localStorage.removeItem(storageKey(leagueId, userId));
  } catch {
    /* ignore */
  }
}

export function useDraftQueue(leagueId, userId) {
  const { draftState } = useSnakeDraft();
  const [queue, setQueue] = useState(() => hydrate(leagueId, userId));

  // Re-hydrate if scope changes (different league or user mid-session).
  useEffect(() => {
    setQueue(hydrate(leagueId, userId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId, userId]);

  // Persist on every queue mutation.
  useEffect(() => {
    persist(leagueId, userId, queue);
  }, [leagueId, userId, queue]);

  // ── Mutators ─────────────────────────────────────────────────────────
  const addToQueue = useCallback(
    (player) => {
      if (!player?.id) return;
      setQueue((prev) => {
        if (prev.some((p) => p.id === player.id)) return prev;
        if (prev.length >= MAX_QUEUE) {
          toast?.("Queue is full (100 max)");
          return prev;
        }
        return [
          ...prev,
          {
            id: player.id,
            name: player.name,
            position: player.position,
            nfl_team: player.nfl_team,
            bye_week: player.bye_week,
            projected_points: player.projected_points,
          },
        ];
      });
    },
    [],
  );

  const removeFromQueue = useCallback((playerId) => {
    setQueue((prev) => prev.filter((p) => p.id !== playerId));
  }, []);

  const reorderQueue = useCallback((fromIdx, toIdx) => {
    setQueue((prev) => {
      if (
        fromIdx === toIdx ||
        fromIdx < 0 ||
        toIdx < 0 ||
        fromIdx >= prev.length ||
        toIdx >= prev.length
      ) {
        return prev;
      }
      const next = prev.slice();
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
  }, []);

  const moveUp = useCallback((playerId) => {
    setQueue((prev) => {
      const idx = prev.findIndex((p) => p.id === playerId);
      if (idx <= 0) return prev;
      const next = prev.slice();
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  }, []);

  const moveDown = useCallback((playerId) => {
    setQueue((prev) => {
      const idx = prev.findIndex((p) => p.id === playerId);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      const next = prev.slice();
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  }, []);

  const clearQueue = useCallback(() => setQueue([]), []);

  const isQueued = useCallback(
    (playerId) => queue.some((p) => p.id === playerId),
    [queue],
  );

  // [Item 2] A queued player drafted by ANY team is REMOVED from the queue.
  // (Reverses TD-121, which kept the row with a red "taken" highlight: with
  // per-row Draft pills and top-of-queue auto-draft, a dead row is just a
  // trap.) A cross-team pick still toasts so the manager knows who took them.
  // Undo does NOT restore pruned entries - the player returns to the pool and
  // can be re-queued.
  // [BUG-A] Source of truth is the COMPLETE drafted set (union of every
  // manager's roster), NOT `pick_history` - which the server trims to the last
  // 30 entries for the wire. Past pick 30 the trimmed window stops growing, so
  // both the old length-delta prune and the old takenIds went stale.
  const draftedByTeam = useMemo(() => {
    const map = new Map();
    const managers = draftState?.managers || {};
    Object.entries(managers).forEach(([teamId, mgr]) => {
      (mgr?.roster || []).forEach((entry) => {
        const pid = entry?.player?.id;
        if (pid) map.set(pid, teamId);
      });
    });
    // Rosters are authoritative; pick_history only backfills a pick whose
    // manager key is missing from the snapshot.
    (draftState?.pick_history || []).forEach((pick) => {
      const pid = pick?.player?.id;
      if (pid && !map.has(pid)) map.set(pid, pick.team_id);
    });
    return map;
  }, [draftState?.managers, draftState?.pick_history]);

  // [Item 2] A queued player drafted by ANY team is REMOVED from the queue.
  // (Reverses TD-121, which kept the row with a red "taken" highlight: with
  // per-row Draft pills and top-of-queue auto-draft, a dead row is just a
  // trap.) A cross-team pick still toasts so the manager knows who took them.
  // Undo does NOT restore pruned entries - the player returns to the pool and
  // can be re-queued.
  const seenDraftedRef = useRef(null);
  const hasSnapshot = !!draftState;
  useEffect(() => {
    if (!hasSnapshot) return; // wait for the first snapshot before baselining
    // First snapshot: baseline the drafted set. A queue rehydrated from
    // localStorage can hold players drafted while this client was away, so
    // they are dropped SILENTLY here - a burst of stale toasts on reconnect
    // would be noise, not news.
    if (seenDraftedRef.current === null) {
      seenDraftedRef.current = new Set(draftedByTeam.keys());
      setQueue((prev) => prev.filter((q) => !draftedByTeam.has(q.id)));
      return;
    }
    const seen = seenDraftedRef.current;
    const newlyDrafted = [];
    draftedByTeam.forEach((teamId, playerId) => {
      if (!seen.has(playerId)) newlyDrafted.push([playerId, teamId]);
    });
    // Re-baseline every pass so an Undo (player leaves the drafted set) lets
    // that player prune again if re-queued and re-drafted.
    seenDraftedRef.current = new Set(draftedByTeam.keys());
    if (!newlyDrafted.length) return;

    setQueue((prev) => {
      if (!prev.length) return prev;
      let next = prev;
      newlyDrafted.forEach(([playerId, teamId]) => {
        const queued = next.find((q) => q.id === playerId);
        if (!queued) return;
        next = next.filter((q) => q.id !== playerId);
        // Self-pick prunes silently - the manager just drafted them.
        if (teamId === userId) return;
        const teamName =
          draftState?.managers?.[teamId]?.team_name || "another team";
        toast?.(
          `${queued.name} taken by ${teamName}.`,
          { duration: 4500 },
        );
      });
      return next;
    });
  }, [draftedByTeam, hasSnapshot, draftState?.managers, userId]);

  // Derived: every player already drafted by ANY team. The auto-draft hook
  // and the per-row Draft pill both read this so a pick that landed between
  // renders can never be submitted twice.
  const takenIds = useMemo(
    () => new Set(draftedByTeam.keys()),
    [draftedByTeam],
  );

  // ── Wipe on terminal state (Gap 6) ───────────────────────────────────
  useEffect(() => {
    if (draftState?.state === "DRAFT_COMPLETE") {
      setQueue([]);
      wipe(leagueId, userId);
    }
  }, [draftState?.state, leagueId, userId]);

  const topOfQueue = queue[0] || null;
  const queueCapped = queue.length >= MAX_QUEUE;

  return useMemo(
    () => ({
      queue,
      addToQueue,
      removeFromQueue,
      reorderQueue,
      moveUp,
      moveDown,
      clearQueue,
      isQueued,
      topOfQueue,
      queueCapped,
      takenIds,
    }),
    [
      queue,
      addToQueue,
      removeFromQueue,
      reorderQueue,
      moveUp,
      moveDown,
      clearQueue,
      isQueued,
      topOfQueue,
      queueCapped,
      takenIds,
    ],
  );
}
